import { useEffect, useState } from "react";
import { Eye, EyeOff, PlugZap, HardDriveDownload } from "lucide-react";
import { MCP_SERVICES, McpService, clearMcpSessions, describeFailure, mcpCallTool } from "../lib/mcp";
import { getServiceTokens, importTokensFromThisPc, isTauri, setServiceTokens } from "../lib/settings";
import { ANTHROPIC_KEY, OPENROUTER_KEY, ANTHROPIC_MODEL, DEFAULT_ANTHROPIC_MODEL, chatStatus, setLocal } from "../lib/chat";
import StatusDot from "../components/StatusDot";
import { PanelScroll } from "./ClientsPanel";

const HEALTH_TOOLS: Record<McpService, { tool: string; args?: Record<string, unknown> }> = {
  core: { tool: "pipeline_freshness" },
  business: { tool: "get_week_brief" },
  financial: { tool: "financial_daily_brief" },
  intel: { tool: "get_reminders", args: { limit: 1 } },
  personal: { tool: "personal_ping" },
  limitless: { tool: "list_recent", args: { limit: 1 } },
};
type TestState = "idle" | "testing" | "ok" | "fail";

export default function ConnectionsPanel({ onKeysChanged }: { onKeysChanged?: () => void }) {
  return (
    <PanelScroll accentVar="--biome-tech" title="Connections" subtitle="data workers · AI keys · device">
      <Workers />
      <AiKeys onKeysChanged={onKeysChanged} />
      <div className="rounded-xl border bg-surface p-4">
        <div className="text-caption uppercase tracking-wide text-text-muted mb-3">This device</div>
        <Row label="Hostname"><span className="font-mono">KYRA-THINKPAD</span></Row>
        <Row label="Shell"><span>{isTauri() ? "Tauri desktop" : "Web browser"}</span></Row>
        <Row label="Fleet doctrine"><span>v2.0</span></Row>
      </div>
    </PanelScroll>
  );
}

function Workers() {
  const [tokens, setTokens] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [tests, setTests] = useState<Record<McpService, { state: TestState; detail?: string; ms?: number }>>(
    () => Object.fromEntries(MCP_SERVICES.map((s) => [s, { state: "idle" }])) as any,
  );
  useEffect(() => setTokens(getServiceTokens()), []);

  const importPc = async () => {
    setImportMsg("importing…");
    try {
      const m = await importTokensFromThisPc();
      setTokens(getServiceTokens());
      clearMcpSessions();
      setImportMsg(`imported ${Object.keys(m).length} worker tokens — reloading…`);
      // Panels fetch on mount; reload so they pick up the new tokens immediately.
      setTimeout(() => location.reload(), 600);
    } catch (e) { setImportMsg(e instanceof Error ? e.message : String(e)); }
  };
  const testAll = async () => {
    setServiceTokens(tokens); clearMcpSessions();
    setTests(Object.fromEntries(MCP_SERVICES.map((s) => [s, { state: "testing" }])) as any);
    await Promise.all(MCP_SERVICES.map(async (s) => {
      const t0 = performance.now();
      const probe = HEALTH_TOOLS[s];
      const r = await mcpCallTool(s, probe.tool, probe.args ?? {}, 12_000);
      const ms = Math.round(performance.now() - t0);
      setTests((p) => ({ ...p, [s]: r.ok ? { state: "ok", ms } : { state: "fail", ms, detail: describeFailure(r.reason, r.detail) } }));
    }));
  };

  return (
    <div className="rounded-xl border bg-surface p-4">
      <div className="text-caption uppercase tracking-wide text-text-muted mb-3">SAGE MCP data workers</div>
      <div className="space-y-2">
        {MCP_SERVICES.map((s) => {
          const t = tests[s];
          return (
            <div key={s} className="flex items-center gap-2">
              <StatusDot health={t.state === "ok" ? "green" : t.state === "fail" ? "red" : "gray"} pulse={t.state === "testing"} />
              <span className="font-mono text-body-sm w-20 shrink-0">{s}</span>
              <input type={revealed ? "text" : "password"} value={tokens[s] ?? ""} onChange={(e) => setTokens((p) => ({ ...p, [s]: e.target.value }))} placeholder={`sage-${s}-mcp bearer`} className="flex-1 h-9 px-3 rounded-md bg-bg text-text border border-strong placeholder:text-text-dim focus:outline-none focus:border-primary text-caption font-mono" />
              <span className="text-caption w-28 truncate shrink-0">
                {t.state === "ok" && <span className="text-success">ok · {t.ms}ms</span>}
                {t.state === "fail" && <span className="text-danger" title={t.detail}>{t.detail}</span>}
                {t.state === "testing" && <span className="text-text-dim">testing…</span>}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button onClick={() => { setServiceTokens(tokens); clearMcpSessions(); setTimeout(() => location.reload(), 300); }} className="h-9 px-4 rounded-md bg-primary text-bg font-medium text-body-sm hover:bg-primary-hover">Save</button>
        <button onClick={testAll} className="flex items-center gap-2 h-9 px-3 rounded-md border border-strong text-body-sm text-text hover:bg-surface-hover"><PlugZap size={16} className="text-primary" />Test connections</button>
        {(isTauri() || import.meta.env.DEV) && <button onClick={importPc} className="flex items-center gap-2 h-9 px-3 rounded-md border border-strong text-body-sm text-text hover:bg-surface-hover"><HardDriveDownload size={16} className="text-accent" />Import from this PC</button>}
        <button onClick={() => setRevealed((r) => !r)} className="flex items-center gap-1.5 h-9 px-3 rounded-md text-text-muted text-body-sm hover:bg-surface-hover">{revealed ? <EyeOff size={15} /> : <Eye size={15} />}{revealed ? "Hide" : "Reveal"}</button>
      </div>
      {importMsg && <div className="mt-2 text-caption text-text-muted">{importMsg}</div>}
    </div>
  );
}

function AiKeys({ onKeysChanged }: { onKeysChanged?: () => void }) {
  const [anthropic, setAnthropic] = useState("");
  const [openrouter, setOpenrouter] = useState("");
  const [model, setModel] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setAnthropic(localStorage.getItem(ANTHROPIC_KEY) ?? "");
    setOpenrouter(localStorage.getItem(OPENROUTER_KEY) ?? "");
    setModel(localStorage.getItem(ANTHROPIC_MODEL) ?? "");
  }, []);
  const save = () => {
    setLocal(ANTHROPIC_KEY, anthropic); setLocal(OPENROUTER_KEY, openrouter); setLocal(ANTHROPIC_MODEL, model);
    setSaved(true); setTimeout(() => setSaved(false), 2000); onKeysChanged?.();
  };
  const st = chatStatus();
  return (
    <div className="rounded-xl border bg-surface p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-caption uppercase tracking-wide text-text-muted">Chat — AI provider</div>
        <StatusDot health={st.provider ? "green" : "gray"} />
        <span className="text-caption text-text-dim">{st.provider ? `${st.provider} · ${st.model}` : "no key — chat disabled"}</span>
      </div>
      <p className="text-body-sm text-text-muted mb-3">The omnipresent chat bar uses Anthropic (preferred) or OpenRouter. Keys stored locally on this device.</p>
      <div className="space-y-2">
        <Field label="Anthropic" value={anthropic} onChange={setAnthropic} revealed={revealed} placeholder="sk-ant-…" />
        <Field label="OpenRouter" value={openrouter} onChange={setOpenrouter} revealed={revealed} placeholder="sk-or-…" />
        <Field label="Model" value={model} onChange={setModel} revealed placeholder={DEFAULT_ANTHROPIC_MODEL} />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button onClick={save} className="h-9 px-4 rounded-md bg-primary text-bg font-medium text-body-sm hover:bg-primary-hover">{saved ? "Saved ✓" : "Save"}</button>
        <button onClick={() => setRevealed((r) => !r)} className="flex items-center gap-1.5 h-9 px-3 rounded-md text-text-muted text-body-sm hover:bg-surface-hover">{revealed ? <EyeOff size={15} /> : <Eye size={15} />}{revealed ? "Hide" : "Reveal"}</button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, revealed, placeholder }: { label: string; value: string; onChange: (v: string) => void; revealed: boolean; placeholder: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-body-sm w-24 shrink-0">{label}</span>
      <input type={revealed ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="flex-1 h-9 px-3 rounded-md bg-bg text-text border border-strong placeholder:text-text-dim focus:outline-none focus:border-primary text-caption font-mono" />
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="flex items-center text-body-sm py-0.5"><span className="w-40 text-text-muted">{label}</span><span className="flex-1 text-text">{children}</span></div>;
}
